/**
 * Single source of truth for all therunup.ai / therunup-* hostname allowlists.
 * Imported by client (web + mobile), shared, and (via mirror) functions.
 *
 * When adding a new hostname:
 * 1. Decide whether it's prod-facing (real users see it) or dev-only
 * 2. Add it to the appropriate constant below
 * 3. Run typecheck — every consumer (auth domain check, DB routing, OAuth
 *    allowlist) re-validates automatically because they all import from here
 *
 * NOTE on functions/: the functions/ workspace tsconfig does not include
 * packages/shared (same constraint as functions/src/types/leads.ts,
 * functions/src/stateTransitions/pipelineAutoAdvance.ts, etc.).
 * functions/src/lib/hostnames.ts maintains a mirror of these constants —
 * keep them byte-identical when editing.
 */

/** Hostnames that map to the prod Firestore database. Used by the
 *  hostname-based getDatabaseId() router (web client picks the DB by
 *  window.location.hostname). */
export const PROD_HOSTNAMES = new Set<string>([
  "therunup.ai",
  "www.therunup.ai",
  "therunup-prod.web.app",
  "therunup-prod.firebaseapp.com",
]);

/** Dev / (default)-database Firebase Hosting domains. Used together with
 *  PROD_HOSTNAMES as the surface set the Next.js web app serves from.
 *
 *  Does NOT include "localhost" — the Auth domain check (see
 *  ALL_RUNTIME_HOSTNAMES below) intentionally excludes localhost because
 *  Firebase Auth's popup handler page is only hosted on real Firebase
 *  domains. On localhost we fall through to therunup-web.web.app as the
 *  handler. The OAuth redirect list (GOOGLE_OAUTH_REDIRECT_ORIGINS) adds
 *  http://localhost:3000 separately because the Next.js dev server uses
 *  a different scheme + port. */
export const DEV_HOSTNAMES = new Set<string>([
  "therunup-web.web.app",
  "therunup-web.firebaseapp.com",
]);

/** All hostnames the web app is served from — used by
 *  packages/web/src/lib/firebase.ts to pick the right `authDomain` for
 *  Firebase Auth. signInWithPopup requires authDomain === window.location
 *  .hostname, so we match the running host against this set and fall back
 *  to therunup-web.web.app when the host isn't recognized (e.g. localhost,
 *  preview URLs). */
export const ALL_RUNTIME_HOSTNAMES = new Set<string>([
  ...PROD_HOSTNAMES,
  ...DEV_HOSTNAMES,
]);

/** Origin URLs (https://hostname) — pre-computed for server-side Origin
 *  header parsing where consumers want full origin strings instead of bare
 *  hostnames. */
export const PROD_ORIGINS = new Set<string>(
  [...PROD_HOSTNAMES].map((h) => `https://${h}`)
);

/** Base URL of the legacy Firebase Hosting `app` target (site `eventify-data`
 *  → `eventify-data.web.app`) that serves the three unauthenticated guest-facing
 *  static pages: `/gift/{code}`, `/photos/{shareToken}`, `/upload/{uploadToken}`.
 *
 *  Single source of truth for every place that mints a link to one of those
 *  pages (gift share links, photo-delivery bodies, photographer upload links).
 *
 *  Intentionally NOT `therunup.ai`: the Next.js prod/dev sites have no
 *  gift/photos/upload routes (they rewrite `**` to Cloud Run), so a link there
 *  would 404. And it is deliberately NOT added to PROD_HOSTNAMES / DEV_HOSTNAMES
 *  because this one domain serves tokens minted in BOTH databases — the backing
 *  giftAPI/photosAPI/uploadAPI functions resolve the owning DB by probing the
 *  token (functions/src/lib/firestore.ts → resolveDbByProbe), not by hostname.
 *  Do not "fix" the brand-domain split by repointing this at therunup.ai without
 *  first giving those pages real routes there. */
export const GUEST_PAGES_BASE_URL = "https://eventify-data.web.app";

/** Origins (scheme + host[+ port]) allowed for Google OAuth redirect URIs.
 *  The Cloud Functions OAuth handler matches the redirect URI passed by
 *  the client byte-for-byte against this list + a fixed callback path.
 *
 *  Uses "http://localhost:3000" (note the port + http scheme — the Next.js
 *  dev server) rather than the bare "localhost" used elsewhere, because
 *  Google requires the same redirect_uri value at token-exchange time
 *  that was used in the authorization request. */
export const GOOGLE_OAUTH_REDIRECT_ORIGINS: readonly string[] = [
  "http://localhost:3000",
  ...[...ALL_RUNTIME_HOSTNAMES].map((h) => `https://${h}`),
];
